/**
 * DRPX Courier — Generischer snake_case → camelCase Mapper
 *
 * Ersetzt 14 manuelle mapXxxFromDb() Funktionen durch:
 * 1. snakeToCamel(obj) — automatische Konvertierung aller Keys
 * 2. createMapper(config) — Factory für tabellen-spezifische Mapper mit Defaults
 *
 * Jeder Mapper behält seinen alten Funktionsnamen und seine Signatur,
 * damit der Rest des Codes NICHT geändert werden muss.
 */

// ════════════════════════════════════════════════════════════════════
// Public types
// ════════════════════════════════════════════════════════════════════

/**
 * Konfiguration für `createMapper()`.
 *
 * Alle Felder sind optional. Leerer Config (`{}`) liefert einen reinen
 * snake→camel-Konverter ohne Transformationen.
 */
export interface MapperConfig {
  /**
   * Default-Werte für fehlende Felder (nach snake→camel angewendet).
   * Beispiel: `{ favoriten: [], adminNotiz: '', festpreis: 0 }`
   */
  defaults?: Record<string, unknown>;
  /**
   * Feldnamen (camelCase), die via `Number()` gecastet werden sollen.
   * Beispiel: `['festpreis', 'preis', 'sumNetto']`
   */
  numbers?: readonly string[];
  /**
   * Feldnamen (camelCase), die via `!!` gecastet werden sollen.
   * Beispiel: `['kleinunternehmer', 'aboPausiert']`
   */
  booleans?: readonly string[];
  /**
   * Hook für berechnete Felder.
   * Bekommt das bereits gemappte Objekt und die Original-Row (snake_case).
   * Das Rückgabe-Objekt wird via `Object.assign` auf das Ergebnis gemerged.
   *
   * Beispiel: `(m, row) => ({ name: row.firma || row.login_name || row.email })`
   */
  computed?: (
    mapped: Record<string, unknown>,
    row: Record<string, unknown>,
  ) => Record<string, unknown>;
}

/**
 * Eine fertig konfigurierte Mapper-Funktion.
 * Nimmt eine DB-Row und liefert das transformierte Objekt (oder `null`
 * bei leerem Input).
 */
export type MapperFn = (
  row: Record<string, unknown> | null | undefined,
) => Record<string, unknown> | null;

// ════════════════════════════════════════════════════════════════════
// snakeToCamel
// ════════════════════════════════════════════════════════════════════

/**
 * Konvertiert alle Keys eines Objekts von snake_case zu camelCase.
 *
 * Beispiel: `{ firma_name: 'ABC', ust_id: 'DE123' }` → `{ firmaName: 'ABC', ustId: 'DE123' }`
 *
 * Defensive Eingaben (`null`, `undefined`, Arrays, Primitives) werden
 * unverändert zurückgegeben — das spart Null-Checks bei den Callern.
 *
 * Die Overloads sorgen dafür, dass Caller den richtigen Return-Typ sehen:
 * - Plain Object rein → `Record<string, unknown>` raus
 * - Alles andere rein → dasselbe raus
 */
// Overload 1: Plain Object → camelCase-Record
export function snakeToCamel(obj: Record<string, unknown>): Record<string, unknown>;
// Overload 2: Alles andere (null, undefined, Array, Primitive) → unverändert
export function snakeToCamel<T>(obj: T): T;
// Implementation
export function snakeToCamel(obj: unknown): unknown {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  const source = obj as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(source)) {
    const camelKey = key.replace(/_([a-z])/g, function (_, c: string) {
      return c.toUpperCase();
    });
    result[camelKey] = source[key];
  }
  return result;
}

// ════════════════════════════════════════════════════════════════════
// camelToSnake
// ════════════════════════════════════════════════════════════════════

/**
 * Konvertiert einen einzelnen camelCase-Key zu snake_case.
 *
 * Beispiel: `firmaName` → `firma_name`, `aboPausiertBis` → `abo_pausiert_bis`
 *
 * Symmetrisches Gegenstück zu `snakeToCamel` — wird bei Update-Operationen
 * verwendet, um App-interne camelCase-Felder in das DB-Format zu bringen
 * (z.B. in `db.updateProfile`). Operiert key-by-key statt auf ganzen
 * Objekten, weil die Caller meist nur eine kontrollierte Whitelist von
 * Feldern weiterreichen wollen — eine ganze-Objekt-Variante würde diese
 * Sicherheits-Whitelist umgehen.
 *
 * Edge-Cases (mit Absicht so):
 * - Bereits snake_case Keys (`firma_name`) bleiben unverändert,
 *   weil sie keine Großbuchstaben enthalten.
 * - Reine lowercase Keys (`id`, `email`) bleiben unverändert.
 * - Führender Großbuchstabe (`Internal` → `_internal`) — symmetrisch zum
 *   `_internal` → `Internal` Edge-Case von `snakeToCamel`.
 *
 * Limitation: Acronyms in camelCase (`IBAN`, `URL`, `KYC`) werden buchstaben-
 * weise zerlegt (`IBAN` → `_i_b_a_n`). In der DRPX-Domain wird das nicht
 * benötigt — Konvention ist `iban`, `kycStatus` etc. Nicht `kYCStatus`.
 *
 * @param key Ein einzelner Key in camelCase (oder bereits snake_case)
 * @returns Der Key in snake_case
 */
export function camelToSnake(key: string): string {
  return key.replace(/[A-Z]/g, function (c: string) {
    return '_' + c.toLowerCase();
  });
}

// ════════════════════════════════════════════════════════════════════
// createMapper
// ════════════════════════════════════════════════════════════════════

/**
 * Factory: Erstellt eine Mapper-Funktion für eine bestimmte Tabelle.
 *
 * Ablauf pro Row:
 *   1. snake_case → camelCase Konvertierung (via `snakeToCamel`)
 *   2. Defaults anwenden (nur bei falsy, aber nicht bei 0 oder false)
 *   3. Number-Casts via `Number()` für konfigurierte Felder
 *   4. Boolean-Casts via `!!` für konfigurierte Felder
 *   5. Berechnete Felder via `computed()`-Hook
 *
 * @param config Mapper-Konfiguration (alle Felder optional)
 * @returns Eine Funktion `mapFromDb(row) → mappedObject | null`
 */
export function createMapper(config: MapperConfig = {}): MapperFn {
  const { defaults = {}, numbers = [], booleans = [], computed = null } = config;

  return function mapFromDb(
    row: Record<string, unknown> | null | undefined,
  ): Record<string, unknown> | null {
    if (!row) return null;

    // 1. Automatische snake_case → camelCase Konvertierung
    const mapped = snakeToCamel(row);

    // 2. Defaults anwenden (wenn Wert falsy — matching original `|| ''` / `|| []` Muster)
    for (const key of Object.keys(defaults)) {
      if (!mapped[key] && mapped[key] !== 0 && mapped[key] !== false) {
        const def = defaults[key];
        // Arrays/Objects müssen kopiert werden damit nicht alle Rows dieselbe Referenz teilen
        mapped[key] = Array.isArray(def)
          ? []
          : typeof def === 'object' && def !== null
            ? Object.assign({}, def)
            : def;
      }
    }

    // 3. Number-Casts
    for (const key of numbers) {
      mapped[key] = Number(mapped[key] || 0);
    }

    // 4. Boolean-Casts
    for (const key of booleans) {
      mapped[key] = !!mapped[key];
    }

    // 5. Berechnete Felder
    if (computed) {
      Object.assign(mapped, computed(mapped, row));
    }

    return mapped;
  };
}
